import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { User } from '../models/user';
import _ from 'lodash';
import { Router } from '@angular/router';
import { RestClientService } from './rest-client.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;

  private users = [
    {
      id: 1,
      username: 'admin',
      password: 'admin',
      firstName: 'Sarah',
      lastName: 'Smith',
      token: 'admin-token',
    },
  ];
  latitude: number | null = null;
  longitude: number | null = null;

  constructor(private http: HttpClient, private router: Router, private restClient: RestClientService) {
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem('currentUser') || '{}')
    );
    this.currentUser = this.currentUserSubject.asObservable();

    this.requestGeolocation();
  }

  private requestGeolocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.latitude = position.coords.latitude;
          this.longitude = position.coords.longitude;
          const pos = { latitude: this.latitude, longitude: this.longitude };
          this.setUserPosition(pos);
          console.log('Captured Lat:', this.latitude, 'Lng:', this.longitude);
        },
        (error) => {
          console.error('Error capturing location:', error);
          // Handle different geolocation error types
          switch(error.code) {
            case error.PERMISSION_DENIED:
              console.warn('Location access denied by user');
              break;
            case error.POSITION_UNAVAILABLE:
              console.warn('Location information unavailable');
              break;
            case error.TIMEOUT:
              console.warn('Location request timed out');
              break;
          }
        }
      );
    } else {
      console.error('Geolocation not supported by this browser.');
    }
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  login(username: string, password: string) {
    const user = this.users.find((u) => u.username === username && u.password === password);

    if (!user) {
      return this.error('Username or password is incorrect');
    } else {
      localStorage.setItem('currentUser', JSON.stringify(user));
      this.currentUserSubject.next(user);
      return this.ok({
        id: user.id,
        username: user.username,
        firstName: user.firstName,
        lastName: user.lastName,
        token: user.token,
      });
    }
  }

  ok(body?: {
    id: number;
    username: string;
    firstName: string;
    lastName: string;
    token: string;
  }) {
    return of(new HttpResponse({ status: 200, body }));
  }

  error(message: string) {
    return throwError(message);
  }

  logout() {
    if (this.getCurrentUser() && this.getCurrentUser().login && this.getCurrentUser().filiale_id) {
      console.log('login out');
      
      // Build request data with optional location
      const requestData: any = {
        login: this.getCurrentUser().login,
        filiale_id: this.getCurrentUser().filiale_id
      };

      // Only include location if available
      if (this.latitude !== null && this.longitude !== null) {
        requestData.latitude = this.latitude;
        requestData.longitude = this.longitude;
      }

      const request = { data: requestData };

      this.restClient
        .post('user/logout', request)
        .subscribe(
          (res) => {
            localStorage.removeItem('currentUser');
            localStorage.removeItem('currentUserPrestataire');
            this.router.navigate(["/authentication/signin"]);
          },
          (err) => {
            localStorage.removeItem('currentUser');
            localStorage.removeItem('currentUserPrestataire');
            this.router.navigate(["/authentication/signin"]);
          }
        );
    } else {
      console.log("already logged out");
      this.router.navigate(["/authentication/signin"]);
    }
  }

  getCurrentUser(): any {
    if (typeof window !== 'undefined' && localStorage) {
      const user = localStorage.getItem('currentUserPrestataire');
      return user ? JSON.parse(user) : null;
    }
    return null;
  }

  isAuth(): boolean {
    if (typeof window !== 'undefined' && localStorage) {
      const user = localStorage.getItem('currentUserPrestataire');
      return user !== null;
    }
    return false;
  }

  getPrestataireActivites(): any {
    if (typeof window !== 'undefined' && localStorage) {
      const user = localStorage.getItem('currentUserPrestataire');
      return user ? JSON.parse(user).activite_utilisateur : [];
    }
    return null;
  }

  getUserActivites(): any {
    if (typeof window !== 'undefined' && localStorage) {
      const user = localStorage.getItem('currentUserPrestataire');
      return user ? JSON.parse(user).activite_utilisateur : [];
    }
    return null;
  }

  getUserAutreActivites(): any {
    if (typeof window !== 'undefined' && localStorage) {
      const user = localStorage.getItem('currentUserPrestataire');
      return user ? JSON.parse(user).activite_utilisateur_autre : [];
    }
    return null;
  }

  getPrestataireActivitesFacturable(): any {
    if (typeof window !== 'undefined' && localStorage) {
      const user = localStorage.getItem('currentUserPrestataire');
      return user ? JSON.parse(user).activite_utilisateur.filter((ap: any) => ap.has_facture == 1) : [];
    }
    return null;
  }

  getPrestataireActivitesPrescription(): any {
    if (typeof window !== 'undefined' && localStorage) {
      const user = localStorage.getItem('currentUserPrestataire');
      return user ? JSON.parse(user).activite_utilisateur.filter((ap: any) => ap.is_prescription == 1) : [];
    }
    return null;
  }

  getPrestataireActivitesExecution(): any {
    if (typeof window !== 'undefined' && localStorage) {
      const user = localStorage.getItem('currentUserPrestataire');
      return user ? JSON.parse(user).activite_utilisateur.filter((ap: any) => ap.is_prescription == 0) : [];
    }
    return null;
  }

  setActiveBeneficiaire(beneficiare: any): any {
    console.log('setting active benef: ', beneficiare);
    return localStorage.setItem('activeBeneficiaire', JSON.stringify(beneficiare));
  }

  public setUserPosition(position: any) {
    if (position) {
      localStorage.setItem('currentUserPositionPrestataire', JSON.stringify(position));
    }
  }

  public getUserPosition() {
    const res = localStorage.getItem('currentUserPositionPrestataire');
    return res ? JSON.parse(res) : null;
  }

  public setUserNavigationData(data: any) {
    if (data) {
      localStorage.setItem('currentUserNavigationPrestataire', JSON.stringify(data));
    }
  }

  public getUserNavigationData() {
    const res = localStorage.getItem('currentUserNavigationPrestataire');
    return res ? JSON.parse(res) : null;
  }

  getActiveBeneficiaire(): any {
    const benef = localStorage.getItem('activeBeneficiaire');
    return benef ? JSON.parse(benef) : null;
  }

  hasAccess(code_activite: string): boolean {
    return this.getUserActivites().some(
      (o: any) => o.codification?.trim().toLowerCase() === code_activite?.trim().toLowerCase() 
    ) || this.getUserAutreActivites().some(
      (o: any) => o.codification?.trim().toLowerCase() === code_activite?.trim().toLowerCase() 
    );
  }
}